import { useMedication } from '@hook'
import { DeleteIcon, PencilIcon } from '@icons'
import { NURSE_MODAL_TYPE, useModalStore } from '@modal'
import { Badge, SideDrawer } from '@ui'
import InformationList from '@ui/InformationList'
import { capitalizeDrugName, concatenateStrengthsConcise, GetFrequencyName } from '@util'
import { Box, Button, Text } from 'grommet'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { renderFrequencyOptions } from './AddNurseMed'

export type Props = {
  onClose: () => void
  defaultData?: any
  id?: string
}

const detailOptions = ['Details', 'Notes', 'Allergies & Interactions']

const NurseMedDetail = ({ onClose, defaultData, id }: Props) => {
  const { openModal } = useModalStore()
  const [tab, setTab] = useState('Details')
  const { medication, includedMed } = useMedication({ id })
  const allergyData = useMemo(() => {
    if (medication && includedMed) {
      return includedMed.find(
        ({ type, id }) => type === 'allergy' && id === medication.relationships?.allergy?.data?.id
      )
    }
    return null
  }, [includedMed, medication])

  const icds = useMemo(() => {
    if (includedMed && allergyData)
      return includedMed.filter(
        ({ id, type }) =>
          type === 'icd_code' && allergyData?.relationships?.icd_codes?.data?.some(icd => icd?.id === id)
      )
    return []
  }, [includedMed, allergyData])

  const allergyDetails = useMemo(() => {
    return {
      'End date': allergyData?.attributes.end_date
        ? moment(allergyData?.attributes.end_date).format('DD/MM/YYYY')
        : '---',
      Reaction: allergyData?.attributes.reaction || '---',
      'Start date': allergyData?.attributes.start_date
        ? moment(allergyData?.attributes.start_date).format('DD/MM/YYYY')
        : '---',
      Status: allergyData?.attributes.status || 'Active'
    }
  }, [allergyData])

  const drug = includedMed?.find(
    ({ id, type }) => type === 'product' && id === medication?.relationships.product.data?.id
  )?.attributes

  const Detail = {
    Dosage: medication?.attributes.dosage || defaultData?.attributes.dosage || '-',
    'End date': defaultData?.attributes?.end_at ? moment(defaultData?.attributes.end_at).format('DD/MM/YYYY') : '-',
    Form: medication?.attributes.form || defaultData?.attributes.form,
    Strength:
      medication?.attributes?.strength ||
      concatenateStrengthsConcise(medication?.attributes.api_doses) ||
      defaultData?.attributes?.strength ||
      concatenateStrengthsConcise(defaultData?.attributes?.api_doses),
    Frequency:
      GetFrequencyName(
        medication?.relationships.medication_frequency.data?.id ||
          defaultData?.relationships?.medication_frequency?.data?.id
      ) || renderFrequencyOptions(medication?.attributes.custom_frequency || defaultData?.attributes?.custom_frequency),
    Medication: capitalizeDrugName(
      drug ? `${drug.generic_name} ${drug.brand_name}` : defaultData?.attributes?.nf_drug_name
    ),
    'Medication type':
      medication?.attributes.medication_type.toUpperCase() || defaultData?.attributes?.medication_type.toUpperCase(),
    Quantity: medication?.attributes.quantity || defaultData?.attributes.quantity || '-',
    'Start date': defaultData?.attributes?.start_at
      ? moment(defaultData?.attributes.start_at).format('DD/MM/YYYY')
      : '-',
    Status: medication?.meta.status || defaultData?.meta?.status
  }

  const header = (
    <Box direction="column" gap={'1em'} pad={{ vertical: '30px' }}>
      <Box align="center" direction="row" gap={'1em'}>
        <Text color="black" size="24px" weight={'bolder'}>
          {Detail?.Medication || 'title'}
        </Text>
        <Badge justify="center" label={defaultData?.meta?.status} size="small" status={defaultData?.meta?.status} />
      </Box>
      <Box direction="row" margin={{ top: '.4em' }}>
        <Button
          className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
          color="#fff"
          icon={<PencilIcon />}
          label={'Edit'}
          margin={{ right: 'small' }}
          onClick={() => {
            openModal(NURSE_MODAL_TYPE.EDIT_MED_MODAL, { defaultData, id })
          }}
          pad={{ horizontal: '10px' }}
          secondary
          style={{ color: 'black', fontSize: '14px', fontWeight: 'normal' }}
          type="button"
        />
        <Button
          className="!border !rounded-[10px] !border-[#E6E6E6] hover:!bg-[#E6E6E6] "
          color="#fff"
          icon={<DeleteIcon alt />}
          label="Delete"
          onClick={() => {
            openModal(NURSE_MODAL_TYPE.DELETE_MEDICATION, { id })
          }}
          pad={{ horizontal: '10px' }}
          secondary
          style={{ color: '#E34949', fontSize: '14px', fontWeight: 'normal' }}
          type="button"
        />
      </Box>
    </Box>
  )

  return (
    <SideDrawer
      header={header}
      onClose={onClose}
      options={detailOptions}
      setTab={setTab}
      style={{ padding: '0 40px' }}
      title="M.A.R Details"
    >
      <Box flex pad={{ horizontal: '20px' }} style={{ overflow: 'hidden', position: 'relative' }}>
        <div
          className={`step-slide ${tab === 'Details' ? 'active' : ''} px-5`}
          style={{
            position: 'absolute',
            transform: `translateX(${tab === 'Details' ? '0' : '-100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          <InformationList data={Detail} />
        </div>
        <div
          className={`step-slide ${tab === 'Notes' ? 'active' : ''} px-5`}
          style={{
            bottom: '0px',
            position: 'absolute',
            top: '0px',
            transform: `translateX(${tab === 'Notes' ? '0' : tab === 'Details' ? '100%' : '-100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          <Box></Box>
        </div>
        <div
          className={`step-slide ${tab === 'Allergies & Interactions' ? 'active' : ''} px-5`}
          style={{
            position: 'absolute',
            transform: `translateX(${tab === 'Allergies & Interactions' ? '0' : '100%'})`,
            transition: 'all 0.3s ease-in-out',
            width: '100%'
          }}
        >
          <InformationList data={allergyDetails} />
          <div className="mt-4">
            <Text>Code</Text>
            <div className="flex flex-wrap gap-x-3 gap-y-3 mt-4">
              {icds?.map(({ attributes: { title } }) => (
                <div className="rounded-full px-5 py-2.5 bg-[#81A16F] text-white" key={title}>
                  {title}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Box>
    </SideDrawer>
  )
}

export default NurseMedDetail
